/**
 * Tool approval query operations - Update
 */

import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredToolApproval } from '../types.js';
import { logger } from '../../../logger.js';

/**
 * Update a tool approval
 */
export async function updateApproval(
  approvalId: string,
  updates: Partial<Omit<StoredToolApproval, 'id' | 'created_at'>>
): Promise<StoredToolApproval> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_tool_approvals')
      .update({
        ...updates,
        updated_at: now,
      })
      .eq('id', approvalId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as StoredToolApproval;
  } catch (error) {
    logger.error('error updating approval', error, { op: 'updateApproval' });
    throw error;
  }
}

/**
 * Approve a tool approval request
 */
export async function approveApproval(
  approvalId: string,
  respondedBy: string
): Promise<StoredToolApproval> {
  return updateApproval(approvalId, {
    status: 'approved',
    responded_at: Date.now(),
    responded_by: respondedBy,
  });
}

/**
 * Deny a tool approval request
 */
export async function denyApproval(
  approvalId: string,
  respondedBy: string
): Promise<StoredToolApproval> {
  return updateApproval(approvalId, {
    status: 'denied',
    responded_at: Date.now(),
    responded_by: respondedBy,
  });
}

/**
 * Mark a tool approval as expired
 */
export async function expireApproval(approvalId: string): Promise<StoredToolApproval> {
  return updateApproval(approvalId, {
    status: 'expired',
  });
}

/**
 * Result of an attempted atomic claim of an approval row for execution.
 *
 * Outcomes:
 * - `{ claimed: true, approval }` – the caller won the race and is the
 *   sole executor for this approval. `approval.execution_claim_id` equals
 *   the supplied marker.
 * - `{ claimed: false, approval }` – the row exists but was not claimable
 *   (already claimed by another executor, status no longer `'approved'`, or
 *   already completed). The current approval state is returned so the
 *   caller can respond idempotently.
 * - `{ claimed: false, approval: null }` – the approval does not exist.
 */
export interface ClaimApprovalResult {
  claimed: boolean;
  approval: StoredToolApproval | null;
}

/**
 * Atomically claim an approval row for execution.
 *
 * Postgres serialises concurrent UPDATEs on the same row, so the conditional
 * update used here can succeed for at most one concurrent caller — the
 * others see zero rows updated and are treated as "not claimed". This makes
 * a double-clicked Slack Approve button safe: only one execute-approved-tool
 * request will run the tool; the other returns the prior in-flight result.
 *
 * Implementation: requires `status = 'approved'` AND `execution_claim_id IS
 * NULL`, then stamps the row with the supplied marker. The `status` column
 * is intentionally NOT changed because the CHECK constraint limits it to
 * `pending | approved | denied | expired` and we want to avoid an enum
 * migration; the new `execution_claim_id` column carries the lock.
 *
 * @param approvalId - The approval ID to claim
 * @param executorMarker - Caller-supplied unique identifier for this
 *   execution attempt (e.g. a UUID). Stored in `execution_claim_id`.
 */
export async function claimApprovalForExecution(
  approvalId: string,
  executorMarker: string
): Promise<ClaimApprovalResult> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    // Conditional update: only succeeds when the row is in 'approved' status
    // and no executor has yet claimed it. Concurrent callers serialise on
    // the row so at most one will get a returning row.
    const { data: claimed, error: claimError } = await client
      .from('meridian_tool_approvals')
      .update({
        execution_claim_id: executorMarker,
        execution_claimed_at: now,
        updated_at: now,
      })
      .eq('id', approvalId)
      .eq('status', 'approved')
      .is('execution_claim_id', null)
      .select()
      .maybeSingle();

    if (claimError) {
      throw claimError;
    }

    if (claimed) {
      return { claimed: true, approval: claimed as StoredToolApproval };
    }

    // No row matched the predicate. Read the current row so the caller can
    // respond idempotently.
    const { data: existing, error: readError } = await client
      .from('meridian_tool_approvals')
      .select('*')
      .eq('id', approvalId)
      .maybeSingle();

    if (readError) {
      throw readError;
    }

    return { claimed: false, approval: (existing as StoredToolApproval) ?? null };
  } catch (error) {
    logger.error('error claiming approval for execution', error, { op: 'claimApprovalForExecution' });
    throw error;
  }
}

/**
 * Mark a previously-claimed approval as completed, recording whether the
 * tool execution succeeded.
 *
 * Only updates the row if `execution_claim_id` matches the supplied marker —
 * preventing one executor from accidentally overwriting another's result.
 */
export async function recordApprovalExecutionResult(
  approvalId: string,
  executorMarker: string,
  succeeded: boolean
): Promise<StoredToolApproval | null> {
  try {
    const client = getSupabaseClient();
    const now = new Date().toISOString();

    const { data, error } = await client
      .from('meridian_tool_approvals')
      .update({
        execution_completed_at: now,
        execution_succeeded: succeeded,
        updated_at: now,
      })
      .eq('id', approvalId)
      .eq('execution_claim_id', executorMarker)
      .select()
      .maybeSingle();

    if (error) {
      throw error;
    }
    return (data as StoredToolApproval) ?? null;
  } catch (error) {
    logger.error('error recording approval execution result', error, { op: 'recordApprovalExecutionResult' });
    return null;
  }
}
